import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Target, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { GoalSuggestions } from './types';

interface GoalSuggestionsResultsProps {
  suggestions: GoalSuggestions;
  onAddGoal?: (title: string, description: string, eType: string) => void;
  onRemoveGoal?: (title: string) => void;
  addedGoalTitles?: Set<string>;
  getETypeColor: (e: string) => string;
  getPriorityColor: (p: string) => string;
}

export const GoalSuggestionsResults = ({
  suggestions,
  onAddGoal,
  onRemoveGoal,
  addedGoalTitles,
  getETypeColor,
  getPriorityColor,
}: GoalSuggestionsResultsProps) => (
  <div className="space-y-4">
    <div className="space-y-3">
      {suggestions.suggested_goals?.map((goal, i) => {
        const isAdded = addedGoalTitles?.has(goal.title);
        return (
          <div key={i} className={cn(
            "p-3 rounded-lg border space-y-2",
            isAdded ? "bg-green-500/5 border-green-500/30" : "bg-muted/30"
          )}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <h5 className="font-medium">{goal.title}</h5>
                <p className="text-sm text-muted-foreground mt-1">{goal.description}</p>
              </div>
              {isAdded ? (
                <button
                  onClick={() => onRemoveGoal?.(goal.title)}
                  className="text-sm text-green-600 hover:text-destructive flex items-center gap-1 shrink-0 transition-colors"
                >
                  <Check className="w-3 h-3" /> Added
                </button>
              ) : onAddGoal ? (
                <Button
                  size="sm"
                  onClick={() => onAddGoal(goal.title, goal.description, goal.e_type)}
                  className="shrink-0 gap-1"
                >
                  <Target className="w-3 h-3" />
                  Add
                </Button>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Badge className={getETypeColor(goal.e_type)} variant="outline">
                {goal.e_type}
              </Badge>
              <Badge className={getPriorityColor(goal.priority)} variant="outline">
                {goal.priority}
              </Badge>
              <span className="text-xs text-muted-foreground">{goal.estimated_duration}</span>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);
